// Building manager — handles purchase + tile assignment. State ownership
// lives in seasonManager (single save blob), so this module just orchestrates.

import { Events, emit, on } from '../ui/eventBus.js'
import { BuildingConfig, PlotConfig, ZoningConfig, KingdomEmperors, RealEstatePrices, PlacementConfig } from '../config/balance.js'
import * as season from './seasonManager.js'
import { getBuildings, isTileBuilt, addBuilding, removeBuilding, getBuilding, setBuildingRotation } from './buildingState.js'
import { getPlotType, isOwned, setPlot } from './plotState.js'
import { PlotTypes } from '../config/balance.js'
import { activeId } from './state.js'
import { formatCoins } from '../ui/format.js'
import { pushLog } from '../ui/hud.js'
import { fieldCoords, parseKey, inField } from '../scene/hexGrid.js'

const TOTAL_TILES = fieldCoords().length

export function init() {
  on(Events.PlacementCommit, ({ type, tileIndex }) => commit(type, tileIndex))
  on(Events.KingdomChange, () => { pending = null }) // switching kingdoms settles any open placement as-is
  broadcast()
}

// Re-emit BuildingAdded for each persisted building so scene + UI repaint.
// Called on boot and after every kingdom switch.
export function broadcast() {
  for (const b of getBuildings()) {
    emit(Events.BuildingAdded, { type: b.type, tileIndex: b.tileIndex, rotation: b.rotation ?? 0 })
  }
}

// CR-5: real-estate purchase price (replaces the old cheap BuildingConfig.cost).
export function priceOf(type) {
  return RealEstatePrices[type] ?? RealEstatePrices.OTHER
}

export function canAfford(type) {
  return season.getWealth() >= priceOf(type)
}

export function freeTilesRemaining() {
  return TOTAL_TILES - getBuildings().length
}

function commit(type, tileIndex) {
  const cfg = BuildingConfig[type]
  if (!cfg) return false
  const { q, r } = parseKey(tileIndex)
  if (!inField(q, r)) return false
  if (isTileBuilt(tileIndex)) return false
  // CR-9: you can only build on land you OWN. Placement already restricts the
  // ghost to owned plots; this guards the commit too.
  if (!isOwned(tileIndex)) return false

  // CR-9: buildings drop on any owned plot. An undeveloped (GRASS) plot is
  // auto-zoned to fit the building (School→Commercial, Granary→Farm, …) so the
  // player never has to zone by hand. A plot already developed for the WRONG
  // use still triggers the Emperor's zoning fine.
  const current = getPlotType(tileIndex)
  const allowed = cfg.allowedPlotTypes
  if (allowed && !allowed.includes(current)) {
    if (current === PlotTypes.GRASS) {
      setPlot(tileIndex, allowed[0]) // auto-zone the cleared plot to suit
    } else {
      fineForZoning(type, tileIndex)
      return false
    }
  }

  const price = priceOf(type)
  if (!season.spendWealth(price)) return false
  addBuilding(type, tileIndex)
  // The purchase is not final yet: the player can rotate it, move it (free) or cancel (refund minus a fee).
  pending = { type, tileIndex, price, plotBefore: current }
  emit(Events.PlacementPending, { type, tileIndex })
  return true
}

// ── Pending placement: Confirm / Reposition / Cancel ──────────────────────────────────────────────────
let pending = null // { type, tileIndex, price, plotBefore }
export const getPending = () => pending
export const cancelRefund = (price) => Math.round(price * PlacementConfig.cancelRefundFrac)

export function rotateBuilding(tileIndex, steps = 1) {
  const b = getBuilding(tileIndex)
  if (!b) return null
  const next = ((b.rotation ?? 0) + steps + PlacementConfig.rotateSteps) % PlacementConfig.rotateSteps
  setBuildingRotation(tileIndex, next)
  return next
}

export function confirmPending() {
  if (!pending) return false
  const p = pending
  pending = null
  pushLog(`${BuildingConfig[p.type]?.name} placed for ${formatCoins(p.price)}. Its facing is now set.`)
  emit(Events.PlacementResolved, { result: 'confirm', tileIndex: p.tileIndex })
  return true
}

// Take the building back off the tile. penalty=true is a Cancel (refund minus the fee); false is a free Reposition.
function takeBack(penalty) {
  const p = pending
  if (!p) return null
  pending = null
  removeBuilding(p.tileIndex)
  if (getPlotType(p.tileIndex) !== p.plotBefore) setPlot(p.tileIndex, p.plotBefore) // undo the auto-zoning
  const refund = penalty ? cancelRefund(p.price) : p.price
  season.earnWealth(refund)
  return { ...p, refund }
}

export function cancelPending() {
  const r = takeBack(true)
  if (!r) return false
  const fee = r.price - r.refund
  pushLog(`Placement cancelled. ${formatCoins(r.refund)} refunded (a ${formatCoins(fee)} cancellation fee was kept).`)
  emit(Events.PlacementResolved, { result: 'cancel', tileIndex: r.tileIndex })
  return true
}

export function repositionPending() {
  const r = takeBack(false)
  if (!r) return false
  emit(Events.PlacementResolved, { result: 'reposition', tileIndex: r.tileIndex })
  emit(Events.PlacementStart, { type: r.type }) // straight back to the cursor, nothing lost
  return true
}

function fineForZoning(type, tileIndex) {
  const cfg = BuildingConfig[type]
  const plotType = getPlotType(tileIndex)
  const plotName = PlotConfig[plotType]?.name ?? 'unowned'
  const fine = priceOf(type) * ZoningConfig.fineMultiplier
  // Emperor's fine — fineWealth takes whatever's available and reports back.
  // Unlike spendWealth it doesn't refuse for insufficient funds.
  const taken = season.fineWealth(fine)
  const emperor = KingdomEmperors[activeId()] ?? KingdomEmperors.WEI
  const body = emperor.zoning?.(cfg.name, plotName, formatCoins(taken))
    ?? `Zoning violation. ${formatCoins(taken)} forfeit.`
  emit(Events.NarrativeBeat, {
    title: `${emperor.name} ${emperor.cn} — Zoning Decree`,
    body,
    kingdom: activeId(),
  })
}
